/**
 * The network side of access: the headscale policy Kronos builds and pushes.
 *
 * `access.ts` decides *who may reach which machine*; this file turns that
 * answer into headscale's policy and decides when to push it. Three ideas:
 *
 *  - **A machine is a tag.** headscale identifies tagged devices by tag, so
 *    "R2" in Kronos is `tag:r2` on the tailnet.
 *  - **The rest of the network is carried, not owned.** The office networks,
 *    the droplet's route to the home PC and everything else that predates
 *    Kronos lives in a *base* policy that Kronos copies through unchanged and
 *    appends its own rules to. The one thing the base may not do is grant
 *    Tailscale SSH to a machine Kronos manages — then revoking in Kronos would
 *    not revoke (`baseConflicts`).
 *  - **The policy is derived, never edited.** It is a function of who may reach
 *    what right now; an ended grant or a deactivated account drops out of the
 *    next build without anyone touching it.
 */

/** One machine, one account, everyone who may log into it (`policyLogins`). */
export interface PolicyLogin {
  readonly tag: string
  readonly user: string
  readonly emails: readonly string[]
}

// --- names -------------------------------------------------------------------

const TAG = /^tag:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const LINUX_USER = /^[a-z_][a-z0-9_-]{0,31}$/

export function isValidTag(tag: string): boolean {
  return TAG.test(tag)
}

export function isValidLinuxUser(user: string): boolean {
  return LINUX_USER.test(user)
}

/**
 * The tag a new machine gets unless somebody types another: "R2" → `tag:r2`,
 * "Office 3090" → `tag:office-3090`. Empty when the name has nothing usable.
 */
export function tagFromName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '')
  return slug ? `tag:${slug}` : ''
}


/**
 * The tag a device already on the network should be managed by. Its own tag
 * when it is the only device carrying it; otherwise one made from its name —
 * two office PCs on `tag:office` are two machines, so each needs its own
 * (`tag:office-3060`), and keeps `tag:office` beside it so the rules that
 * name the office still apply.
 */
export function tagForDevice(device: { readonly name: string; readonly tags: readonly string[] }, others: readonly { readonly tags: readonly string[] }[]): {
  readonly tag: string
  readonly added: boolean
} {
  const only = device.tags.find((t) => others.every((o) => !o.tags.includes(t)))
  if (device.tags.length === 1 && only) return { tag: only, added: false }
  return { tag: tagFromName(device.name), added: true }
}

// --- machines ----------------------------------------------------------------

export type MachineRefusal = 'no_name' | 'bad_tag' | 'tag_taken' | 'bad_user' | 'base_grants_ssh'

export const MACHINE_REFUSAL_MESSAGE: Readonly<Record<MachineRefusal, string>> = {
  no_name: 'give the machine a name',
  bad_tag: 'a tag is tag: followed by lower-case letters, digits and dashes',
  tag_taken: 'another machine already has that tag',
  bad_user: 'the login user is a Linux account name — lower-case letters, digits, _ and -',
  base_grants_ssh:
    'the other network rules already give SSH to that tag, so revoking here would not revoke — remove that rule first',
}

export function refuseMachine(input: {
  readonly name: string
  readonly tag: string
  readonly sshUser: string
  readonly takenTags: readonly string[]
  readonly base: PolicyDoc | null
}): MachineRefusal | null {
  if (!input.name.trim()) return 'no_name'
  if (!isValidTag(input.tag)) return 'bad_tag'
  if (input.takenTags.includes(input.tag)) return 'tag_taken'
  if (!isValidLinuxUser(input.sshUser)) return 'bad_user'
  if (input.base && baseConflicts(input.base, [input.tag]).some((c) => c.kind === 'ssh')) return 'base_grants_ssh'
  return null
}

// --- HuJSON ------------------------------------------------------------------

/**
 * headscale's policy is HuJSON: JSON with `//` and `/* *\/` comments and
 * trailing commas. This turns it into plain JSON, leaving strings alone — a
 * URL in a string contains `//`, and a regex that misses that eats half a rule.
 */
export function hujsonToJson(text: string): string {
  let noComments = ''
  let i = 0
  while (i < text.length) {
    const c = text[i] as string
    if (c === '"') {
      const end = endOfString(text, i)
      noComments += text.slice(i, end)
      i = end
    } else if (c === '/' && text[i + 1] === '/') {
      while (i < text.length && text[i] !== '\n') i++
    } else if (c === '/' && text[i + 1] === '*') {
      const close = text.indexOf('*/', i + 2)
      i = close === -1 ? text.length : close + 2
      noComments += ' '
    } else {
      noComments += c
      i++
    }
  }

  let out = ''
  i = 0
  while (i < noComments.length) {
    const c = noComments[i] as string
    if (c === '"') {
      const end = endOfString(noComments, i)
      out += noComments.slice(i, end)
      i = end
      continue
    }
    if (c === ',') {
      let j = i + 1
      while (j < noComments.length && /\s/.test(noComments[j] as string)) j++
      if (noComments[j] === '}' || noComments[j] === ']') {
        i++
        continue
      }
    }
    out += c
    i++
  }
  return out
}

function endOfString(text: string, start: number): number {
  let i = start + 1
  while (i < text.length) {
    if (text[i] === '\\') i += 2
    else if (text[i] === '"') return i + 1
    else i++
  }
  return text.length
}

// --- the policy --------------------------------------------------------------

/** A headscale policy, parsed. Only the three sections Kronos writes are typed. */
export interface PolicyDoc {
  readonly tagOwners?: Readonly<Record<string, readonly string[]>>
  readonly acls?: readonly unknown[]
  readonly ssh?: readonly unknown[]
  readonly [section: string]: unknown
}

export class PolicyParseError extends Error {}

export function parsePolicy(text: string): PolicyDoc {
  let value: unknown
  try {
    value = JSON.parse(hujsonToJson(text))
  } catch (e) {
    throw new PolicyParseError(`not valid HuJSON: ${(e as Error).message}`)
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new PolicyParseError('a policy is an object')
  }
  const doc = value as Record<string, unknown>
  for (const key of ['acls', 'ssh'] as const) {
    if (doc[key] !== undefined && !Array.isArray(doc[key])) throw new PolicyParseError(`"${key}" must be a list`)
  }
  const owners = doc.tagOwners
  if (owners !== undefined && (typeof owners !== 'object' || owners === null || Array.isArray(owners))) {
    throw new PolicyParseError('"tagOwners" must be an object')
  }
  return doc as PolicyDoc
}

/**
 * Rules in the base that already reach a machine Kronos manages.
 *
 *  - `ssh`: a Tailscale SSH rule whose destination is the tag. **Blocking** —
 *    Kronos would revoke someone and they would keep SSH through this rule.
 *  - `network`: an ACL letting someone reach the tag's ports. A warning: it
 *    opens the machine's own sshd (password or key), not Tailscale SSH, which
 *    is outside what Kronos controls — but an admin should know it is there.
 */
export interface BaseConflict {
  readonly kind: 'ssh' | 'network'
  readonly tag: string
  /** Index into the base's `ssh` or `acls` list. */
  readonly rule: number
}

export function baseConflicts(base: PolicyDoc, tags: readonly string[]): readonly BaseConflict[] {
  const out: BaseConflict[] = []
  const dsts = (rule: unknown): readonly string[] => {
    const d = (rule as { dst?: unknown } | null)?.dst
    return Array.isArray(d) ? d.filter((x): x is string => typeof x === 'string') : []
  }
  ;(base.ssh ?? []).forEach((rule, i) => {
    for (const tag of tags) if (dsts(rule).includes(tag)) out.push({ kind: 'ssh', tag, rule: i })
  })
  ;(base.acls ?? []).forEach((rule, i) => {
    for (const tag of tags) if (dsts(rule).some((d) => d === tag || d.startsWith(`${tag}:`))) out.push({ kind: 'network', tag, rule: i })
  })
  return out
}

/**
 * Hand a tag over to Kronos: the base with every rule that reaches it taken
 * out — Tailscale SSH rules naming it, and ACLs opening its ports. A rule that
 * also names other things keeps them; one left naming nothing is dropped.
 * This is what "remove that rule" does when the base already gives SSH to a
 * machine being added (`baseConflicts`).
 *
 * The result is plain JSON: HuJSON comments do not survive a parse, so the
 * commented version stays in the history of the base rather than in force.
 */
export function releaseTag(base: PolicyDoc, tag: string): { readonly policy: PolicyDoc; readonly removed: number } {
  let removed = 0
  const strip = (rules: readonly unknown[] | undefined, hits: (d: string) => boolean): unknown[] | undefined => {
    if (!rules) return undefined
    const out: unknown[] = []
    for (const rule of rules) {
      const dst = (rule as { dst?: unknown } | null)?.dst
      if (!Array.isArray(dst)) {
        out.push(rule)
        continue
      }
      const kept = dst.filter((d) => !(typeof d === 'string' && hits(d)))
      if (kept.length === dst.length) out.push(rule)
      else {
        removed++
        if (kept.length > 0) out.push({ ...(rule as object), dst: kept })
      }
    }
    return out
  }
  const acls = strip(base.acls, (d) => d === tag || d.startsWith(`${tag}:`))
  const ssh = strip(base.ssh, (d) => d === tag)
  const policy: Record<string, unknown> = { ...base }
  if (acls !== undefined) policy.acls = acls
  if (ssh !== undefined) {
    if (ssh.length) policy.ssh = ssh
    else delete policy.ssh
  }
  return { policy: policy as PolicyDoc, removed }
}

/**
 * The whole policy: the base, unchanged, with Kronos's rules appended.
 *
 *  - Every live machine's tag is defined in `tagOwners` with **no owners**,
 *    whatever the base says: no person or device can claim it, so a machine is
 *    tagged only by Kronos. Owners in the base would let any of them tag their
 *    own laptop `tag:r2` and receive the connections meant for R2. A retired
 *    machine's tag stays defined (its devices may still carry it, and an
 *    undefined tag makes headscale reject the whole policy) but keeps whatever
 *    owners the base gives it — retiring "Office PCs" on `tag:office` must not
 *    take the office tag away from the office rules.
 *  - Machines reachable by **exactly the same people as the same account**
 *    share one ACL (port 22) and one Tailscale SSH rule; Tailscale SSH needs
 *    both. At 200 robots in a handful of groups that is a handful of rules, not
 *    four hundred near-identical ones.
 *
 * `tags` is every managed machine's tag; `logins` says who logs into which
 * as which account (`policyLogins` in `access.ts`). Deterministic — tags and
 * emails sorted — so the same inputs give the same text and a push happens
 * only when something changed.
 */
export function buildPolicy(input: {
  readonly base: PolicyDoc
  readonly tags: readonly string[]
  readonly retiredTags?: readonly string[]
  readonly logins: readonly PolicyLogin[]
}): PolicyDoc {
  const byTag = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

  const tagOwners: Record<string, readonly string[]> = { ...input.base.tagOwners }
  for (const tag of [...(input.retiredTags ?? [])].sort(byTag)) if (!(tag in tagOwners)) tagOwners[tag] = []
  for (const tag of [...input.tags].sort(byTag)) tagOwners[tag] = []

  const buckets = new Map<string, { emails: readonly string[]; user: string; tags: string[] }>()
  const logins = [...input.logins].sort((a, b) => byTag(a.tag, b.tag) || byTag(a.user, b.user))
  for (const l of logins) {
    const emails = [...new Set(l.emails)].sort()
    if (emails.length === 0) continue
    const key = `${l.user}\n${emails.join(',')}`
    const bucket = buckets.get(key) ?? { emails, user: l.user, tags: [] }
    if (!bucket.tags.includes(l.tag)) bucket.tags.push(l.tag)
    buckets.set(key, bucket)
  }
  // Order rules by their first tag, so the text reads in machine order.
  const rules = [...buckets.values()].sort((a, b) => byTag(a.tags[0] as string, b.tags[0] as string))

  const acls: unknown[] = [...(input.base.acls ?? [])]
  const ssh: unknown[] = [...(input.base.ssh ?? [])]
  for (const r of rules) {
    acls.push({ action: 'accept', src: [...r.emails], dst: r.tags.map((t) => `${t}:22`) })
    ssh.push({ action: 'accept', src: [...r.emails], dst: [...r.tags], users: [r.user] })
  }

  return { ...input.base, tagOwners, acls, ssh }
}

export const POLICY_HEADER =
  '// Generated by Kronos (Connectivity) — edits made here are overwritten.\n' +
  '// Change access, and the other network rules, in Kronos.\n'

/** What is pushed to headscale, byte for byte — and what drift is judged against. */
export function policyText(policy: PolicyDoc): string {
  return `${POLICY_HEADER}${JSON.stringify(policy, null, 2)}\n`
}

// --- pushing -----------------------------------------------------------------

export type PushReason = 'first' | 'changed' | 'drift'

export type ReconcileDecision = { readonly push: false } | { readonly push: true; readonly reason: PushReason }

/**
 * Whether to push, and why.
 *
 * `live` is what headscale holds now (null when it holds nothing);
 * `lastPushed` is the text Kronos last pushed successfully (null: never).
 * When the live policy is neither what Kronos wants nor what Kronos last put
 * there, somebody edited headscale directly — that is `drift`, and it is
 * overwritten like any other change, but recorded as such so an admin can see
 * it happened.
 */
export function decideReconcile(input: {
  readonly desired: string
  readonly live: string | null
  readonly lastPushed: string | null
}): ReconcileDecision {
  if (input.live === input.desired) return { push: false }
  if (input.lastPushed === null) return { push: true, reason: 'first' }
  if (input.live === input.lastPushed) return { push: true, reason: 'changed' }
  return { push: true, reason: 'drift' }
}

